/**
 * Fase 3 — Prøvesett steg 6: Oppsummering + lagring.
 * Viser alle valg og lar bruker trigge en av final-handlingene.
 */
import { Award, FileCheck, Shield, Database, BookOpen, CalendarCheck } from 'lucide-react';
import type { ExamSetWizardData } from '../NewExamSetWizard';
import SebTerm from '../SebTerm';

export interface SaveStepProps {
  data: ExamSetWizardData;
  studyPlanTitle?: string;
  moduleTitles?: string[];
  rubricTitle?: string | null;
}

export default function SaveStep({ data, studyPlanTitle, moduleTitles, rubricTitle }: SaveStepProps) {
  const scale = {
    pass_fail: 'Bestått / Ikke bestått',
    three_tier: 'Trenivå (MGB / B / IB)',
    a_f: 'A–F',
    percentage: 'Prosent 0–100',
  }[data.grading_scale];

  const ASSESSMENT_TYPE_LABELS: Record<string, React.ReactNode> = {
    written: (<>Skriftlig (<SebTerm variant="bare" />)</>),
    oral: 'Muntlig',
    project: 'Prosjektoppgave',
    practical: 'Praktisk demonstrasjon',
    portfolio: 'Mappevurdering',
    observation: 'Kontinuerlig observasjon',
    group: 'Gruppearbeid',
    other: 'Annen vurderingsform',
  };
  const assessmentType: React.ReactNode = data.components.length > 1
    ? <>Kompositt ({data.components.length} komponenter — {data.composite_mode === 'weighted_composite' ? 'vektet' : 'alle må bestås'})</>
    : (ASSESSMENT_TYPE_LABELS[data.primary_assessment_type] ?? 'Ukjent');

  return (
    <div className="space-y-5">
      <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900">
        <p className="font-semibold mb-1">Oppsummering før lagring</p>
        <p>
          Gå gjennom valgene under og velg en lagringshandling. Du kan alltid redigere prøvesettet etter opprettelse via
          "Rediger prøvesett" i Prøvesystem-fanen.
        </p>
      </div>

      <SummarySection
        icon={<BookOpen className="w-5 h-5 text-kf-blue-600" />}
        title="Grunninfo"
        items={[
          { label: 'Studieplan', value: studyPlanTitle || data.study_plan_id || '(ikke valgt)' },
          { label: 'Tittel', value: data.title || '(mangler)' },
          { label: 'Beskrivelse', value: data.description || '(ingen)' },
          {
            label: 'Modul-dekning',
            value: data.module_coverage === 'full'
              ? 'Hele studieplanen'
              : `${data.included_module_ids.length} valgte moduler${moduleTitles?.length ? ': ' + moduleTitles.join(', ') : ''}`,
          },
          { label: 'Attest-gyldighet', value: data.validity_years ? `${data.validity_years} år` : 'Evig gyldig' },
        ]}
      />

      <SummarySection
        icon={<Award className="w-5 h-5 text-kf-blue-600" />}
        title="Vurdering"
        items={[
          { label: 'Vurderingsform', value: assessmentType },
          { label: 'Tidsbegrensning', value: data.time_limit_minutes ? `${data.time_limit_minutes} min` : 'Ubegrenset' },
          { label: 'Beståttgrense', value: `${data.pass_threshold}%` },
          { label: 'Karakterskala', value: scale },
        ]}
      />

      <SummarySection
        icon={<FileCheck className="w-5 h-5 text-kf-blue-600" />}
        title="Matrise og rammeverk"
        items={[
          { label: 'Vurderingsmatrise', value: rubricTitle || '(ingen)' },
          {
            label: 'Rammeverk-begrunnelse',
            value: data.framework_rationale ? `${data.framework_rationale.length} tegn` : '(ingen)',
          },
        ]}
      />

      <SummarySection
        icon={<Shield className="w-5 h-5 text-kf-blue-600" />}
        title="Teknisk oppsett"
        items={[
          {
            label: 'Sikker eksamensnettleser (SEB)',
            value: data.seb_required ? 'På (anbefalt)' : 'Av',
            emphasis: data.seb_required ? 'ok' : 'warn',
          },
          {
            label: 'Spørsmålskilde',
            value: data.use_question_bank
              ? `Bank med smart randomisering (${data.question_count} spm/kandidat)`
              : 'Statiske spørsmål',
            emphasis: data.use_question_bank ? 'ok' : 'warn',
          },
          data.use_question_bank
            ? {
                label: 'Vanskelighetsfordeling',
                value: `${Math.round(data.difficulty_distribution.easy * 100)}% enkel · ${Math.round(
                  data.difficulty_distribution.medium * 100,
                )}% middels · ${Math.round(data.difficulty_distribution.hard * 100)}% vanskelig`,
              }
            : null,
        ].filter(Boolean) as { label: string; value: React.ReactNode; emphasis?: 'ok' | 'warn' }[]}
      />

      {data.primary_assessment_type === 'written' && (
        <SummarySection
          icon={<Database className="w-5 h-5 text-kf-blue-600" />}
          title="Spørsmål"
          items={
            data.use_question_bank
              ? [{ label: 'Modus', value: 'Spørsmålsbank — spørsmål trekkes dynamisk per kurs' }]
              : [{ label: 'Antall statiske', value: `${data.questions.length} spørsmål lagret` }]
          }
        />
      )}

      <div className="rounded-md bg-kf-blue-50 border border-kf-blue-200 p-3 text-sm text-kf-blue-900 flex items-start gap-2">
        <CalendarCheck className="w-5 h-5 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium mb-1">Neste steg etter lagring</p>
          <p>
            Trykk <strong>"Lagre og start kurs-veiviser"</strong> hvis du vil opprette et konkret kurs som bruker dette
            prøvesettet nå. Trykk <strong>"Lagre og publiser"</strong> for å gjøre prøvesettet tilgjengelig uten å
            opprette kurs. <strong>"Lagre som utkast"</strong> beholder redigeringsmodus — ikke tilgjengelig for kurs.
          </p>
        </div>
      </div>
    </div>
  );
}

interface SummarySectionProps {
  icon: React.ReactNode;
  title: string;
  items: { label: string; value: React.ReactNode; emphasis?: 'ok' | 'warn' }[];
}

function SummarySection({ icon, title, items }: SummarySectionProps) {
  return (
    <div className="rounded-lg border border-kf-gray-200 bg-white">
      <div className="px-4 py-2 border-b border-kf-gray-200 bg-kf-gray-50 flex items-center gap-2">
        {icon}
        <h4 className="font-semibold text-kf-gray-900 text-sm">{title}</h4>
      </div>
      <dl className="px-4 py-3 text-sm space-y-1.5">
        {items.map((item, i) => (
          <div key={i} className="flex items-start gap-3">
            <dt className="text-kf-gray-600 min-w-[150px] shrink-0">{item.label}:</dt>
            <dd
              className={`flex-1 min-w-0 ${
                item.emphasis === 'warn'
                  ? 'text-amber-700 font-medium'
                  : item.emphasis === 'ok'
                  ? 'text-emerald-700'
                  : 'text-kf-gray-900'
              }`}
            >
              {item.value || '(ikke satt)'}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

